import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { useAuthStore } from 'app/stores/StoreContext'
import { ClientType } from 'app/generated/graphql'
import { notify, notifyError } from 'app/services/notify.client'
import { safeRedirect } from 'app/utils'
import { debug, logError } from 'lib/logging'

export type LoginActionData = {
  error?: string
  getCode?: boolean
  channel?: string
  email?: string
  password?: string
  tryOtherLogin?: boolean
}

export type UseLoginReturn = {
  email: string
  setEmail: (email: string) => void
  password: string
  setPassword: (password: string) => void
  loading: boolean
  setLoading: (loading: boolean) => void
  isSubmitting: boolean
  redirectTo: string
  actionData: LoginActionData | undefined
  codeFormRef: React.RefObject<HTMLFormElement | null>
  channelRef: React.RefObject<HTMLInputElement | null>
  otpValue: string
  setOtpValue: (value: string) => void
  resend: () => void
  backToLogin: () => void
  handleLogin: () => Promise<void>
  handleVerifyOtp: (code?: string) => Promise<void>
}

export function useLogin(): UseLoginReturn {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const auth = useAuthStore()
  const redirectTo = safeRedirect(searchParams.get('redirectTo') || '/')
  const codeFormRef = useRef<HTMLFormElement>(null)
  const channelRef = useRef<HTMLInputElement>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [otpValue, setOtpValue] = useState('')
  const [pendingToken, setPendingToken] = useState<string | undefined>()
  const [actionData, setActionData] = useState<LoginActionData | undefined>(
    searchParams.has('otp') ? { getCode: true, channel: 'SMS', email: 'test@test.com', password: 'test' } : undefined,
  )

  debug('login', 'redirect after login: ', redirectTo)

  async function handleLogin() {
    setLoading(true)
    setActionData(undefined)
    try {
      const result = await auth.login(email, password, ClientType.Pwa)
      if (!result) {
        setActionData({ error: (auth.error || 'Login failed').replace(/^\[GraphQL\]\s*/, '') })
        return
      }
      if (result.pendingToken) {
        // 2FA required
        setPendingToken(result.pendingToken)
        setActionData({ getCode: true, channel: result.channel, email, password })
      } else if (result.accessToken) {
        await auth.fetchMe()
        navigate(redirectTo)
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed'
      logError(`Login failed`, err)
      setActionData({ error: msg })
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyOtp(code?: string) {
    const otp = code || otpValue
    if (!pendingToken || !otp) return
    setLoading(true)
    try {
      const result = await auth.verifyOtp(pendingToken, otp, ClientType.Pwa)
      if (result?.accessToken) {
        await auth.fetchMe()
        navigate(redirectTo)
      } else {
        notifyError(auth.error || 'Invalid code')
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Invalid code'
      logError(`OTP verification failed`, err)
      notifyError(msg)
    } finally {
      setLoading(false)
    }
  }

  async function resend() {
    if (!actionData?.email || !actionData?.password) return
    setLoading(true)
    try {
      const result = await auth.login(actionData.email, actionData.password, ClientType.Pwa)
      if (result?.pendingToken) {
        setPendingToken(result.pendingToken)
        notify('Code resent')
      }
    } finally {
      setLoading(false)
    }
  }

  function backToLogin() {
    // We're already on /login during 2FA, so navigating to /login is a no-op.
    // Reset the OTP flow state to return to the credentials form instead.
    setActionData(undefined)
    setOtpValue('')
    setPassword('')
    setPendingToken(undefined)
  }

  return {
    email,
    setEmail,
    password,
    setPassword,
    loading,
    setLoading,
    isSubmitting: loading,
    redirectTo,
    actionData,
    codeFormRef,
    channelRef,
    otpValue,
    setOtpValue,
    resend,
    backToLogin,
    handleLogin,
    handleVerifyOtp,
  }
}
